import {useEffect, useRef, useState} from "react";
import {Plus, Save, Trash2, Pencil, X, Image as ImageIcon, Loader2, Star, EyeOff, Eye} from "lucide-react";
import {apiGet, apiPost, apiPut, apiDelete, apiUpload} from "@/lib/api";
import {MARKET_CATEGORIES} from "@/lib/constants";

const EMPTY = {name: "", brand: "", brand_details: "", category: MARKET_CATEGORIES[0], description: "", highlights: [], images: [], price: "", mrp: "", variants: "", availability: "In stock", location: "", shipping: "", returns: "", tags: [], details: {}, is_live: true};
const FIELDS = [["name", "Product name"], ["brand", "Maker / brand"], ["price", "Price (e.g. ₹4,500)"], ["mrp", "MRP (optional)"], ["availability", "Availability"], ["variants", "Sizes / variants"], ["location", "Made in / ships from"], ["shipping", "Shipping"], ["returns", "Returns"]];

function Reviews({id, notify}) {
  const [list, setList] = useState(null);
  useEffect(() => { apiGet(`/listings/${id}/reviews`).then(setList).catch(() => setList([])); }, [id]);
  const del = async (rid) => { try { await apiDelete(`/admin/reviews/${rid}`); setList(l => l.filter(r => r.review_id !== rid)); notify("Review removed"); } catch { notify("Could not remove"); } };
  if (!list) return null;
  return (
    <div className="ap-reviews" data-testid={`ap-reviews-${id}`}>
      {list.length === 0 ? <small>No reviews yet.</small> : list.map(r => (
        <div key={r.review_id} className="ap-review"><span><Star size={11} fill="currentColor"/> {r.rating} · <b>{r.author}</b> — {r.text}</span>
          <button data-testid={`ap-review-delete-${r.review_id}`} onClick={() => del(r.review_id)}><Trash2 size={13}/></button></div>
      ))}
    </div>
  );
}

function Editor({initial, onSaved, close, notify}) {
  const [f, setF] = useState({...EMPTY, ...initial, highlights: initial.highlights || [], images: initial.images?.length ? initial.images : initial.image ? [initial.image] : []});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF(p => ({...p, [k]: v}));
  const upload = async (files) => {
    setBusy(true);
    try { for (const file of Array.from(files)) { const r = await apiUpload(file); setF(p => ({...p, images: [...p.images, r.url].slice(0, 8)})); } }
    catch { notify("Upload failed"); } finally { setBusy(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.keys(EMPTY).map(k => [k, f[k]]));
    try {
      const r = initial.listing_id ? await apiPut(`/admin/products/${initial.listing_id}`, body) : await apiPost("/admin/products", body);
      onSaved(r); notify("Product saved");
    } catch (err) { notify(err?.response?.data?.detail?.[0]?.msg || "Please check the fields"); }
  };
  return (
    <form className="admin-panel wide ap-editor" onSubmit={save} data-testid="ap-editor">
      <div className="admin-panel-head"><h2>{initial.listing_id ? "Edit product" : "New product"}</h2><button type="button" data-testid="ap-close" className="text-button" onClick={close}><X size={16}/></button></div>
      <div className="cp-fields">
        {FIELDS.map(([k, l]) => <label key={k}>{l}<input data-testid={`ap-${k}`} required={k === "name"} value={f[k]} onChange={e => set(k, e.target.value)}/></label>)}
        <label>Category<select data-testid="ap-category" value={f.category} onChange={e => set("category", e.target.value)}>{MARKET_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></label>
      </div>
      <label className="ap-wide">Description<textarea data-testid="ap-description" rows={4} value={f.description} onChange={e => set("description", e.target.value)}/></label>
      <label className="ap-wide">Highlights (one per line)<textarea data-testid="ap-highlights" rows={3} value={f.highlights.join("\n")} onChange={e => set("highlights", e.target.value.split("\n").map(x => x.trimStart()).filter((x, i, a) => x || i === a.length - 1))}/></label>
      <label className="ap-wide">About the maker (optional)<textarea data-testid="ap-brand-details" rows={2} value={f.brand_details} onChange={e => set("brand_details", e.target.value)}/></label>
      <div className="ap-images">
        {f.images.map((src, i) => <div key={src + i} className="fc-thumb"><img src={src} alt=""/><button type="button" onClick={() => set("images", f.images.filter((_, j) => j !== i))}><X size={11}/></button></div>)}
        <input ref={fileRef} hidden type="file" accept="image/*" multiple data-testid="ap-image-input" onChange={e => upload(e.target.files)}/>
        <button type="button" data-testid="ap-add-image" className="outline-button sm" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? <Loader2 size={13} className="spin"/> : <ImageIcon size={13}/>} Add photos</button>
      </div>
      <label className="pref-row check"><input type="checkbox" data-testid="ap-live" checked={f.is_live} onChange={e => set("is_live", e.target.checked)}/> Show in Bazaar</label>
      <button data-testid="ap-save" className="coral-button sm" type="submit"><Save size={13}/> Save product</button>
    </form>
  );
}

export default function ProductsAdmin({notify}) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => apiGet("/admin/products").then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const hide = async (p) => {
    try { p.is_live === false ? await apiPut(`/admin/products/${p.listing_id}`, {...EMPTY, ...p, is_live: true}) : await apiDelete(`/admin/products/${p.listing_id}`); load(); notify(p.is_live === false ? "Product is live again" : "Product hidden from Bazaar"); }
    catch { notify("Could not update"); }
  };
  return (
    <div data-testid="cp-products">
      {edit ? <Editor initial={edit} notify={notify} close={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }}/> : (
        <section className="admin-panel wide">
          <div className="admin-panel-head"><div><div className="eyebrow">BAZAAR</div><h2>Products ({items.length})</h2></div>
            <button data-testid="ap-new" className="coral-button sm" onClick={() => setEdit({})}><Plus size={13}/> Add product</button></div>
          <p className="admin-help">Only admins add products. Members can rate and review; you can remove any review here.</p>
          {items.map(p => (
            <div key={p.listing_id}>
              <div className="ap-row" data-testid={`ap-row-${p.listing_id}`}>
                {(p.images?.[0] || p.image) && <img className="ap-img" src={p.images?.[0] || p.image} alt=""/>}
                <div><strong>{p.name}</strong><small>{p.category} · {p.price || "No price"} · {p.is_live === false ? "Hidden" : "Live"}</small></div>
                <button data-testid={`ap-reviews-toggle-${p.listing_id}`} onClick={() => setOpen(open === p.listing_id ? null : p.listing_id)}><Star size={14}/></button>
                <button data-testid={`ap-edit-${p.listing_id}`} onClick={() => setEdit(p)}><Pencil size={14}/></button>
                <button data-testid={`ap-hide-${p.listing_id}`} onClick={() => hide(p)}>{p.is_live === false ? <Eye size={14}/> : <EyeOff size={14}/>}</button>
              </div>
              {open === p.listing_id && <Reviews id={p.listing_id} notify={notify}/>}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
